<template>
  <div class="form-container">
    <h1 class="page-title">建物マスタ情報新規作成</h1>
    <form @submit.prevent="handleSubmit" @keydown.enter.prevent>
      <div class="mb-3">
        <label for="customer" class="form-label">顧客 <span class="badge text-bg-danger">必須</span></label>
        <CustomerSelectBox
          v-model="building.m_customer_id"
          :options="customers"
          :required="true"
        />
      </div>

      <div class="mb-3">
        <label for="buildingType" class="form-label">区分 <span class="badge text-bg-danger">必須</span></label>
        <BuildingTypeSelectBox
          v-model="building.m_building_type_id"
          :options="buildingTypes"
          :required="true"
        />
      </div>

      <div class="mb-3">
        <label for="buildingName" class="form-label">建物名 <span class="badge text-bg-danger">必須</span></label>
        <input v-model="building.building_nm" type="text" placeholder="建物名" class="form-control" id="buildingName" required>
        <div v-if="errorMessages.building_nm" class="alert alert-danger mt-2">{{ errorMessages.building_nm[0] }}</div>
      </div>

      <PostalCodeInput
        v-model="building.postal_code"
        @address="({ region, locality, street }) => {
          building.prefecture = region;
          building.city = locality;
          building.other_address = street;
        }"
      />

      <div class="mb-3">
        <label for="prefecture" class="form-label">都道府県</label>
        <input v-model="building.prefecture" type="text" placeholder="都道府県" class="form-control" id="prefecture">
        <div v-if="errorMessages.prefecture" class="alert alert-danger mt-2">{{ errorMessages.prefecture[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="city" class="form-label">市区町村</label>
        <input v-model="building.city" type="text" placeholder="市区町村" class="form-control" id="city">
        <div v-if="errorMessages.city" class="alert alert-danger mt-2">{{ errorMessages.city[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="otherAddress" class="form-label">その他の住所</label>
        <input v-model="building.other_address" type="text" placeholder="その他の住所" class="form-control" id="otherAddress">
        <div v-if="errorMessages.other_address" class="alert alert-danger mt-2">{{ errorMessages.other_address[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="telNum" class="form-label">電話番号</label>
        <input v-model="building.tel_num" type="text" placeholder="電話番号" class="form-control" id="telNum">
        <div v-if="errorMessages.tel_num" class="alert alert-danger mt-2">{{ errorMessages.tel_num[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="contractDt" class="form-label">契約日</label>
        <input
          v-model="building.contract_dt"
          type="date"
          id="contractDt"
          class="form-control"
          max="2099-12-31"
          placeholder="契約日"
        >
      </div>

      <div class="mb-3">
        <label for="inChargeNm" class="form-label">担当者</label>
        <input v-model="building.in_charge_nm" type="text" placeholder="担当者" class="form-control" id="inChargeNm">
        <div v-if="errorMessages.in_charge_nm" class="alert alert-danger mt-2">{{ errorMessages.in_charge_nm[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="inChargePhoneNum" class="form-label">携帯番号</label>
        <input v-model="building.in_charge_phone_num" type="text" placeholder="携帯番号" class="form-control" id="inChargePhoneNum">
        <div v-if="errorMessages.in_charge_phone_num" class="alert alert-danger mt-2">{{ errorMessages.in_charge_phone_num[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="inChargeEmail" class="form-label">担当者メールアドレス</label>
        <input v-model="building.in_charge_email" type="text" placeholder="担当者メールアドレス" class="form-control" id="inChargeEmail">
        <div v-if="errorMessages.in_charge_email" class="alert alert-danger mt-2">{{ errorMessages.in_charge_email[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="fixed_fee_billing" class="ECM_CheckboxInput">
          <input
            v-model="building.fixed_fee_billing"
            :true-value="true"
            :false-value="false"
            id="fixed_fee_billing"
            type="checkbox"
            class="ECM_CheckboxInput-Input"
          >
          <span class="ECM_CheckboxInput-DummyInput"></span>
          <h4 class="ms-3">定額請求対象</h4>
        </label>
      </div>

      <div class="mb-3">
        <label for="user" class="form-label">社内担当者 <span class="badge text-bg-danger">必須</span></label>
        <UserSelectBox
          v-model="building.internal_person_id"
          :options="users"
          :required="true"
        />
      </div>

      <div class="mb-3">
        <label for="contractEndDt" class="form-label">契約終了日</label>
        <input
          v-model="building.contract_end_dt"
          type="date"
          id="contractEndDt"
          class="form-control"
          max="2099-12-31"
          placeholder="契約終了日"
        >
      </div>

      <div class="btn-area">
        <a class="btn btn-secondary mx-1" @click="$router.back()">戻る</a>
        <button type="submit" class="btn btn-primary mx-1" :disabled="isSubmitting">登録</button>
      </div>
    </form>

    <DuplicateNameConfirmationModal
      :modalId="duplicateModalId"
      message="すでに同名の建物名が登録されていますが、本当に作成してもよろしいでしょうか？"
      @confirm="handleDuplicateConfirm"
    />
  </div>
</template>

<script>
import { ref, onMounted, watchEffect } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { Modal } from 'bootstrap';
import {
  checkBuildingDuplicate,
  getSelectBoxCustomers,
  getBuildingTypes,
  getSelectBoxUsers,
  createBuilding
} from '@/api/axios';
import CustomerSelectBox from '@/components/searchSelectBoxes/CustomerSelectBox.vue';
import BuildingTypeSelectBox from '@/components/searchSelectBoxes/BuildingTypeSelectBox.vue';
import UserSelectBox from '@/components/searchSelectBoxes/UserSelectBox.vue';
import DuplicateNameConfirmationModal from '@/components/shared/DuplicateNameConfirmationModal.vue';
import PostalCodeInput from '@/components/shared/PostalCodeInput.vue';

export default {
  components: {
    CustomerSelectBox,
    BuildingTypeSelectBox,
    UserSelectBox,
    DuplicateNameConfirmationModal,
    PostalCodeInput
  },
  setup() {
    const title = ref('建物情報新規作成');

    watchEffect(() => {
      document.title = title.value;
    });

    const customers = ref([]);
    const buildingTypes = ref([]);
    const users = ref([]);
    const building = ref({
      m_customer_id: '',
      m_building_type_id: '',
      building_nm: '',
      postal_code: '',
      prefecture: '',
      city: '',
      other_address: '',
      tel_num: '',
      contract_dt: '',
      in_charge_nm: '',
      in_charge_phone_num: '',
      in_charge_email: '',
      fixed_fee_billing: '',
      contract_end_dt: '',
      internal_person_id: ''
      // 他のフィールド
    });

    // エラーメッセージ用のリアクティブプロパティ
    const errorMessages = ref({});
    const isSubmitting = ref(false);
    const duplicateModalId = 'duplicateBuildingCreateModal';

    const store = useStore();
    const router = useRouter();
    const route = useRoute();

    const fetchCustomerAndBuildingTypes = async () => {
      try {
        const [customerResponse, buildingTypeResponse, userResponse] = await Promise.all([
          getSelectBoxCustomers(),
          getBuildingTypes(),
          getSelectBoxUsers()
        ]);

        customers.value = customerResponse.data;
        buildingTypes.value = buildingTypeResponse.data.building_types;
        users.value = userResponse.data;
      } catch (error) {
        console.error('顧客情報・建物種別情報・ユーザ情報の取得に失敗しました:', error);
      }
    };

    const showDuplicateModal = () => {
      const modalElement = document.getElementById(duplicateModalId);
      const modal = Modal.getInstance(modalElement) || new Modal(modalElement);
      modal.show();
    };

    const saveBuilding = async () => {
      if (isSubmitting.value) return;

      try {
        isSubmitting.value = true;
        await createBuilding(building.value);
        store.dispatch('setFlashMessage', {message: '建物情報作成完了！', type: 'success'});
        router.push({
          path: '/buildings',
          query: route.query
        });
      } catch (error) {
        if (error.response && error.response.data) {
          // エラーメッセージをerrorMessagesにセット
          errorMessages.value = error.response.data || {};
        }

        store.dispatch('setFlashMessage', {message: '建物情報作成失敗！', type: 'danger'});
        console.error('作成エラー:', error);
      } finally {
        isSubmitting.value = false;
      }
    };

    const handleSubmit = async () => {
      if (isSubmitting.value) return;

      try {
        isSubmitting.value = true;
        const response = await checkBuildingDuplicate(building.value.building_nm);
        if (response.data?.duplicate) {
          showDuplicateModal();
          return;
        }
      } catch (error) {
        console.error('重複チェックエラー:', error);
      } finally {
        isSubmitting.value = false;
      }

      await saveBuilding();
    };

    const handleDuplicateConfirm = async () => {
      await saveBuilding();
    };

    onMounted(fetchCustomerAndBuildingTypes);

    return {
      title,
      customers,
      buildingTypes,
      users,
      building,
      handleSubmit,
      handleDuplicateConfirm,
      errorMessages,
      isSubmitting,
      duplicateModalId
    };
  }
};
</script>
